/** Matches backend `MigrationDriver.GLOBAL_MENU`. */
export const GLOBAL_MENU_DRIVER = 'GlobalMenu';

export function isGlobalLinkedItem(migrationDriver) {
  return migrationDriver === GLOBAL_MENU_DRIVER;
}

/** Recipes are editable on global menus console, or on local-only restaurant items. */
export function isRecipesEditable(isGlobalMenuContext, migrationDriver) {
  return isGlobalMenuContext || !isGlobalLinkedItem(migrationDriver);
}

function buildModifierCatalogIndexes(allModifiers = []) {
  const modifierGroupIds = new Set();
  const optionIdToModifierGroupId = new Map();

  (allModifiers || []).forEach((modifier) => {
    if (modifier?.id != null) {
      modifierGroupIds.add(modifier.id);
    }
    (modifier.options || modifier.modifierOptions || []).forEach((option) => {
      if (option?.id != null) {
        optionIdToModifierGroupId.set(option.id, modifier.id);
      }
    });
  });

  return { modifierGroupIds, optionIdToModifierGroupId };
}

function isModifierGroupObject(child, indexes) {
  if (!child || typeof child !== 'object') {
    return false;
  }
  if (child.modifier != null) {
    return false;
  }
  if (Array.isArray(child.options) || Array.isArray(child.modifierOptions)) {
    return true;
  }
  return child.id != null && indexes.modifierGroupIds.has(child.id);
}

/**
 * ModifierOption.modifiers_children M2M expects nested Modifier group ids (not option ids).
 * Pass `allModifiers` when available so option-shaped legacy values resolve to their parent group.
 */
export function resolveNestedModifierId(child, allModifiers = []) {
  if (child == null) {
    return null;
  }

  const indexes = buildModifierCatalogIndexes(allModifiers);
  const hasCatalog = indexes.modifierGroupIds.size > 0 || indexes.optionIdToModifierGroupId.size > 0;

  if (typeof child === 'number') {
    if (indexes.modifierGroupIds.has(child)) {
      return child;
    }
    if (indexes.optionIdToModifierGroupId.has(child)) {
      return indexes.optionIdToModifierGroupId.get(child);
    }
    return child;
  }

  if (typeof child !== 'object') {
    return null;
  }

  if (child.modifier != null) {
    return child.modifier;
  }

  if (isModifierGroupObject(child, indexes)) {
    return child.id;
  }

  if (child.id != null) {
    if (indexes.optionIdToModifierGroupId.has(child.id)) {
      return indexes.optionIdToModifierGroupId.get(child.id);
    }
    if (!hasCatalog && (child.title != null || child.name != null)) {
      return null;
    }
  }

  return child.id;
}

export function serializeNestedModifierIds(modifiersChildren, allModifiers = []) {
  if (!modifiersChildren?.length) {
    return [];
  }
  return modifiersChildren
    .map((child) => resolveNestedModifierId(child, allModifiers))
    .filter((id) => id != null);
}

/** First nested modifier group id from a UI selection or modifiers_children array. */
export function nestedModifierGroupIdFromSelection(selection, allModifiers = []) {
  if (selection == null) {
    return null;
  }
  const children = Array.isArray(selection) ? selection : [selection];
  return serializeNestedModifierIds(children, allModifiers)[0] ?? null;
}

/** Resolve nested group for display/editing (full modifier from catalog when possible). */
export function resolveNestedModifierForDisplay(modifiersChildren, allModifiers = []) {
  const groupId = serializeNestedModifierIds(modifiersChildren, allModifiers)[0];
  if (groupId == null) {
    return undefined;
  }
  const fromCatalog = allModifiers?.find((modifier) => modifier.id === groupId);
  if (fromCatalog) {
    return fromCatalog;
  }
  const embedded = modifiersChildren?.find(
    (child) => typeof child === 'object' && child?.id === groupId && isModifierGroupObject(child, buildModifierCatalogIndexes(allModifiers)),
  );
  return embedded || { id: groupId };
}

/** Modifier groups attached to an item with normalized `options` arrays. */
export function resolveAttachedModifiers(attachedModifiers = [], allModifiers = []) {
  const byId = new Map((allModifiers || []).map((modifier) => [modifier.id, modifier]));
  return (attachedModifiers || []).map((modifier) => {
    const full = byId.get(modifier.id) || modifier;
    return {
      ...full,
      title: full.title || full.name,
      options: full.options || full.modifierOptions || modifier.options || modifier.modifierOptions || [],
    };
  });
}

function resolveNestedModifierGroup(child, modifiersById) {
  if (child == null) {
    return null;
  }
  if (typeof child === 'number') {
    return modifiersById.get(child) || null;
  }
  if (child.options?.length || child.modifierOptions?.length) {
    return child;
  }
  if (child.id != null) {
    return modifiersById.get(child.id) || child;
  }
  return null;
}

/** Nested groups under a parent option (from modifiers_children or options_parents fallback). */
function nestedModifierGroupsForOption(option, modifiersById, allModifiers = []) {
  const fromChildren = (option.modifiers_children || [])
    .map((child) => resolveNestedModifierGroup(child, modifiersById))
    .filter(Boolean);
  if (fromChildren.length) {
    return fromChildren;
  }
  return (allModifiers || []).filter(
    (modifier) => (modifier.options_parents || []).includes(option.id),
  );
}

/** Leaf selectable options only — skip parent options that only open a nested modifier group. */
export function collectModifierOptionIds(modifiers = [], allModifiers = []) {
  const modifiersById = new Map((allModifiers || []).map((modifier) => [modifier.id, modifier]));
  const ids = [];
  const seen = new Set();

  const addId = (optionId) => {
    if (optionId != null && !seen.has(optionId)) {
      seen.add(optionId);
      ids.push(optionId);
    }
  };

  const walkModifier = (modifier) => {
    if (!modifier) {
      return;
    }
    (modifier.options || modifier.modifierOptions || []).forEach((option) => {
      const nestedGroups = nestedModifierGroupsForOption(option, modifiersById, allModifiers);
      if (nestedGroups.length) {
        nestedGroups.forEach((nestedModifier) => walkModifier(nestedModifier));
      } else {
        addId(option.id);
      }
    });
  };

  (modifiers || []).forEach((modifier) => walkModifier(modifier));
  return ids;
}

export function collectModifierOptionRows(modifiers = [], allModifiers = []) {
  const modifiersById = new Map((allModifiers || []).map((modifier) => [modifier.id, modifier]));
  const rows = [];

  const walkModifier = (modifier, pathPrefix = '') => {
    if (!modifier) {
      return;
    }
    const groupTitle = modifier.title || modifier.name;
    const nextPrefix = pathPrefix ? `${pathPrefix} → ${groupTitle}` : groupTitle;

    (modifier.options || modifier.modifierOptions || []).forEach((option) => {
      const optionTitle = option.title || option.name;
      const nestedGroups = nestedModifierGroupsForOption(option, modifiersById, allModifiers);

      if (nestedGroups.length) {
        const branchPrefix = `${nextPrefix} → ${optionTitle}`;
        nestedGroups.forEach((nestedModifier) => walkModifier(nestedModifier, branchPrefix));
      } else {
        rows.push({
          modifierOptionId: option.id,
          optionTitle,
          modifierTitle: nextPrefix,
        });
      }
    });
  };

  (modifiers || []).forEach((modifier) => walkModifier(modifier));
  return rows;
}

export function pruneModifierOptionRecipes(recipes = [], allowedOptionIds) {
  const allowed = allowedOptionIds instanceof Set ? allowedOptionIds : new Set(allowedOptionIds);
  return (recipes || []).filter((entry) => {
    const optionId = entry.modifier_option ?? entry.modifier_option_id;
    return optionId != null && allowed.has(optionId);
  });
}

export function buildItemRecipeQuantityByIngredient(itemRecipeLines = []) {
  const map = {};
  (itemRecipeLines || []).forEach((line) => {
    const ingredientId = line?.ingredient;
    const quantity = Number(line?.quantity);
    if (ingredientId != null && !Number.isNaN(quantity) && quantity > 0) {
      map[ingredientId] = quantity;
    }
  });
  return map;
}

/** Validates negative modifier-option lines against the item's base recipe. */
export function validateModifierOptionRecipes(modifierOptionRecipes = [], itemRecipeLines = []) {
  const itemQtyByIngredient = buildItemRecipeQuantityByIngredient(itemRecipeLines);
  const errors = [];

  (modifierOptionRecipes || []).forEach((entry) => {
    (entry.recipe_lines || []).forEach((line) => {
      const ingredientId = line?.ingredient;
      const quantity = Number(line?.quantity);
      const label = line?.ingredient_name || `Ingredient #${ingredientId}`;

      if (ingredientId == null || Number.isNaN(quantity) || quantity >= 0) {
        return;
      }

      if (!(ingredientId in itemQtyByIngredient)) {
        errors.push(
          `${label}: add this ingredient to the item recipe before using a negative modifier quantity.`,
        );
        return;
      }

      if (Math.abs(quantity) > itemQtyByIngredient[ingredientId]) {
        errors.push(
          `${label}: negative quantity cannot exceed the item recipe amount (${itemQtyByIngredient[ingredientId]}).`,
        );
      }
    });
  });

  return { valid: errors.length === 0, errors };
}

export function normalizeModifierOptionRecipesFromApi(entries = []) {
  return (entries || []).map((entry) => ({
    modifier_option: entry.modifier_option ?? entry.modifier_option_id,
    recipe_lines: (entry.recipe_lines || []).map((line) => ({
      ...line,
      ingredient: line.ingredient,
      quantity: line.quantity,
    })),
  }));
}

export function serializeModifierOptionRecipes(entries = [], itemRecipeLines = []) {
  const { valid } = validateModifierOptionRecipes(entries, itemRecipeLines);
  if (!valid) {
    return [];
  }
  return (entries || [])
    .map((entry) => {
      const modifierOption = entry.modifier_option ?? entry.modifier_option_id;
      const recipe_lines = serializeRecipeLines(entry.recipe_lines, { allowNonPositive: true });
      if (!modifierOption || !recipe_lines.length) {
        return null;
      }
      return { modifier_option: modifierOption, recipe_lines };
    })
    .filter(Boolean);
}

export function serializeRecipeLines(lines, { allowNonPositive = false } = {}) {
  return (lines || [])
    .filter((line) => {
      if (!line?.ingredient) {
        return false;
      }
      const quantity = Number(line.quantity);
      if (Number.isNaN(quantity)) {
        return false;
      }
      return allowNonPositive ? quantity !== 0 : quantity > 0;
    })
    .map((line) => ({
      ingredient: line.ingredient,
      quantity: line.quantity,
    }));
}
